import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight, Pencil, Plus } from "lucide-react";
import { getRealizadoSaldoCompetencias } from "../../api";
import { ModalCompetencia } from "../../components/gestaoRealizado/FormCompetencia";
import RealizadoLayout from "../../components/gestaoRealizado/RealizadoLayout";
import {
  AvisoErro, Badge, BarraFiltros, Card, Carregando, GradeKpis, Kpi, Seletor, Vazio, botaoPequeno, estiloTabela,
  formatCompacto, formatCompetencia, formatData, formatValor, opcoesEmpresa, useCarregar, useTema,
} from "../../components/gestaoRealizado/comum";

// Saldo de competência = valor da competência − Σ baixas (NF, cancelamento, repasse e as
// solicitações do formulário marcadas "É competência? = Sim" — colunas Nota Fiscal 1–4).
// Calculado, nunca digitado. A coluna "Planilha" mostra o Saldo Bruto da aba Controle comp.
// na última carga, só para conferência — divergência vira SALDO_DIVERGENTE no log.
// Competências novas são cadastradas aqui (botão "Nova competência"), não mais na planilha.

const NOMES_BAIXA = { NF: "NF", CANCELAMENTO: "Cancelamento", REPASSE: "Repasse", SEM_NF: "Sem nº de NF", OUTRO: "Outro", SOLICITACAO: "Solicitação (competência)" };

export default function SaldoCompetenciasRealizado() {
  const [isLight, setIsLight, C] = useTema();
  const [apenasAberto, setApenasAberto] = useState("true");
  const [empresa, setEmpresa] = useState("");
  const [abertos, setAbertos] = useState(() => new Set());
  const [modal, setModal] = useState(null); // null | "nova" | linha em edição
  const { dados, carregando, erro, recarregar } = useCarregar(() => getRealizadoSaldoCompetencias(apenasAberto, empresa), [apenasAberto, empresa]);
  const T = estiloTabela(C);
  const linhas = dados || [];
  const positivos = linhas.filter((l) => l.saldoBruto > 0.005);
  const divergentes = linhas.filter((l) => l.saldoPlanilha !== null && Math.abs(l.saldoPlanilha - l.saldoBruto) > 0.01);

  const alternar = (id) => setAbertos((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  return (
    <RealizadoLayout titulo="Saldo de competência" isLight={isLight} setIsLight={setIsLight}>
      <BarraFiltros>
        <Seletor C={C} rotulo="Mostrar" value={apenasAberto} onChange={setApenasAberto} opcoes={[
          { value: "true", label: "Só com saldo" }, { value: "false", label: "Todas as competências" },
        ]} />
        <Seletor C={C} rotulo="Empresa" value={empresa} onChange={setEmpresa} opcoes={opcoesEmpresa} />
      </BarraFiltros>
      <AvisoErro C={C}>{erro}</AvisoErro>

      {carregando && !dados ? <Carregando C={C} /> : (
        <>
          <GradeKpis>
            <Kpi C={C} rotulo="Saldo a faturar" valor={formatCompacto(positivos.reduce((a, l) => a + l.saldoBruto, 0))} detalhe={`${positivos.length} competência(s) em aberto`} cor={C.warning} />
            <Kpi C={C} rotulo="Divergências com a planilha" valor={divergentes.length} detalhe="Saldo calculado ≠ Saldo Bruto da planilha" cor={divergentes.length ? C.danger : C.success} />
          </GradeKpis>
          <Card
            C={C}
            titulo={`${linhas.length} competência(s)`}
            subtitulo="Clique na linha para ver as baixas (NFs, cancelamentos, repasses)"
            acoes={<button style={botaoPequeno(C.brand)} onClick={() => setModal("nova")}><Plus size={13} /> Nova competência</button>}
          >
            {linhas.length === 0 ? <Vazio C={C}>Nenhuma competência com saldo. Cadastre uma em “Nova competência”.</Vazio> : (
              <div style={{ ...T.wrap, maxHeight: "68vh" }}>
                <table style={{ ...T.table, minWidth: 1250 }}>
                  <thead>
                    <tr>
                      <th style={T.th} />
                      <th style={T.th}>Projeto</th>
                      <th style={T.th}>Empresa</th>
                      <th style={T.th}>GP</th>
                      <th style={T.th}>Competência</th>
                      <th style={{ ...T.th, ...T.thNum }}>Valor</th>
                      <th style={{ ...T.th, ...T.thNum }}>Baixado</th>
                      <th style={{ ...T.th, ...T.thNum }}>Saldo</th>
                      <th style={{ ...T.th, ...T.thNum }}>Planilha</th>
                      <th style={{ ...T.th, ...T.thNum }}>Aging (dias)</th>
                      <th style={T.th}>Expectativa fat.</th>
                      <th style={T.th} />
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l) => {
                      const divergente = l.saldoPlanilha !== null && Math.abs(l.saldoPlanilha - l.saldoBruto) > 0.01;
                      const aberto = abertos.has(l.id);
                      return (
                        <Fragment key={l.id}>
                          <tr onClick={() => alternar(l.id)} style={{ cursor: "pointer" }}>
                            <td style={{ ...T.td, width: 20, color: C.muted }}>{aberto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                            <td style={{ ...T.td, fontStyle: l.projetoId ? "normal" : "italic" }}>{l.projeto}{l.entrega ? <span style={{ color: C.muted }}> · {l.entrega}</span> : null}</td>
                            <td style={T.td}>{l.empresa || "—"}</td>
                            <td style={T.td}>{l.gp || "—"}</td>
                            <td style={T.td}>{formatCompetencia(l.competencia)}</td>
                            <td style={{ ...T.td, ...T.tdNum }}>{formatValor(l.valorCompetencia)}</td>
                            <td style={{ ...T.td, ...T.tdNum }}>{formatValor(l.valorBaixado)}</td>
                            <td style={{ ...T.td, ...T.tdNum, fontWeight: 700, color: l.saldoBruto < -0.005 ? C.danger : l.saldoBruto > 0.005 ? C.warning : C.text }}>{formatValor(l.saldoBruto)}</td>
                            <td style={{ ...T.td, ...T.tdNum, color: divergente ? C.danger : C.muted }} title={divergente ? "Diferente do saldo calculado" : undefined}>
                              {l.saldoPlanilha === null ? "—" : formatValor(l.saldoPlanilha)}{divergente ? " ⚠" : ""}
                            </td>
                            <td style={{ ...T.td, ...T.tdNum }}>{l.saldoBruto > 0.005 ? l.agingDias : "—"}</td>
                            <td style={T.td}>{formatCompetencia(l.expectativaFaturamento)}</td>
                            <td style={{ ...T.td, whiteSpace: "nowrap" }}>
                              {l.origem === "PLATAFORMA" && <Badge cor={C.brand} title={l.criadoPor ? `Criada por ${l.criadoPor}` : undefined}>Plataforma</Badge>}
                              <button
                                onClick={(e) => { e.stopPropagation(); setModal(l); }}
                                title="Editar competência"
                                style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, padding: 4, marginLeft: 4, verticalAlign: "middle" }}
                              >
                                <Pencil size={14} />
                              </button>
                            </td>
                          </tr>
                          {aberto && (
                            <tr>
                              <td />
                              <td colSpan={11} style={{ ...T.td, background: C.bg }}>
                                {l.baixas.length === 0 ? <span style={{ color: C.muted }}>Nenhuma baixa registrada.</span> : (
                                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                    {l.baixas.map((b, i) => (
                                      <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                        <Badge cor={b.tipo === "NF" || b.tipo === "SOLICITACAO" ? C.success : b.tipo === "CANCELAMENTO" ? C.danger : C.warning}>{NOMES_BAIXA[b.tipo] || b.tipo}</Badge>
                                        {b.nf && <span>NF {b.nf}</span>}
                                        {b.tipo === "SOLICITACAO" && <span style={{ color: C.muted }}>{b.texto}</span>}
                                        <span style={{ fontVariantNumeric: "tabular-nums" }}>R$ {formatValor(b.valor)}</span>
                                        {b.data && <span style={{ color: C.muted }}>em {formatData(b.data)}</span>}
                                        {b.texto && !["NF", "SOLICITACAO"].includes(b.tipo) && <span style={{ color: C.muted }}>“{b.texto}”</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div style={{ color: C.muted, marginTop: 6 }}>
                                  Cliente: {l.cliente || "—"} · {l.origem === "PLATAFORMA" ? `Cadastrada em ${formatData(l.dataSolicitacao)}${l.criadoPor ? ` por ${l.criadoPor}` : ""}` : `Solicitada em ${formatData(l.dataSolicitacao)}`}
                                  {l.atualizadoPor && ` · editada por ${l.atualizadoPor} em ${formatData(l.atualizadoEm)}`}
                                  {l.observacao && ` · ${l.observacao}`}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
      {modal && (
        <ModalCompetencia
          C={C}
          competencia={modal === "nova" ? null : modal}
          onClose={() => setModal(null)}
          onSalvo={() => { setModal(null); recarregar(); }}
        />
      )}
    </RealizadoLayout>
  );
}
